/**
 * The townsfolk (data/folk.ts) and the carriage station master: plain working clothes, no weapons.
 */
import type { Look } from './types';
import { LOOKS } from './heroes';

/** The townsfolk (data/folk.ts): plain working clothes, no weapons. */
const FOLK_BASE: Look = { ...LOOKS.merchant!, beard: undefined, beardColor: undefined, pendant: undefined };
/** ลุงไห่: an old fisherman, white-bearded under a bamboo hat, in a faded tunic. */
LOOKS.fisherOld = {
  ...FOLK_BASE,
  age: 'elder',
  hair: '#c8c4bc',
  hairH: '#e8e4dc',
  hairStyle: 'topknot',
  headwear: 'none',
  hat: { c: '#c4a468', s: '#8a6e3e' },
  outer: '#5a7488',
  outerS: '#3e5466',
  outerH: '#7a94a8',
  trim: '#2e3e4c',
  inner: '#d8d0bc',
  innerS: '#a89e88',
  sash: '#6a4a2a',
  sashS: '#4a3018',
  boots: '#3a2e24',
  bootsS: '#241c16',
  shortRobe: true,
  beard: 'long',
  beardColor: '#e8e4dc',
  faceStyle: 'refSmirk',
};
/** เสี่ยวหยู: the fisherman's grandson, barefoot-quick in a red tunic. */
LOOKS.fisherKid = {
  ...FOLK_BASE,
  age: 'child',
  hair: '#1a1410',
  hairH: '#3a2a20',
  hairStyle: 'messy',
  headwear: 'none',
  outer: '#b8443a',
  outerS: '#8a2e26',
  outerH: '#d8665a',
  trim: '#5a1a14',
  inner: '#e8d8b8',
  innerS: '#b8a888',
  sash: '#3a2a20',
  sashS: '#241a14',
  shortRobe: true,
  blush: '#f0a0a0',
  faceStyle: 'refSmirk',
};
/** นางหลิว: the innkeeper, hair pinned up, a plum robe over a cream skirt. */
LOOKS.innkeeper = {
  ...FOLK_BASE,
  female: true,
  hair: '#2a1a1a',
  hairH: '#4a2e2a',
  hairStyle: 'bun',
  headwear: 'pin',
  crown: '#d8a83a',
  crownS: '#a07a20',
  outer: '#8a3a5a',
  outerS: '#622840',
  outerH: '#aa5a7a',
  trim: '#e8c870',
  inner: '#f0e4cc',
  innerS: '#c8b89c',
  sash: '#e8c870',
  sashS: '#a8883a',
  blush: '#f0a6a6',
  faceStyle: 'refSoft',
};
/** อาหนิว: a broad farmer in a sleeveless vest and a straw hat. */
LOOKS.farmer = {
  ...FOLK_BASE,
  build: 'stocky',
  skin: '#d8a478',
  skinS: '#a87650',
  hair: '#1a1410',
  hairH: '#3a2a20',
  hairStyle: 'topknot',
  headwear: 'none',
  hat: { c: '#d8bc78', s: '#a08848' },
  outer: '#7a6a4a',
  outerS: '#54482e',
  outerH: '#9a8a66',
  trim: '#4a3e28',
  inner: '#5a6a7a',
  innerS: '#3e4a58',
  sash: '#3a3020',
  sashS: '#241e14',
  shortRobe: true,
  sleeveless: true,
  beard: 'stubble',
  faceStyle: 'refSerious',
};
/** ป้าซุน: a farm wife in indigo, sleeves rolled, under a straw hat. */
LOOKS.farmerAunt = {
  ...FOLK_BASE,
  female: true,
  skin: '#e2b48e',
  skinS: '#b8885f',
  hair: '#2a2020',
  hairH: '#4a3a34',
  hairStyle: 'bun',
  headwear: 'none',
  hat: { c: '#d8bc78', s: '#a08848' },
  outer: '#3e4a7a',
  outerS: '#2a3356',
  outerH: '#5a6a9a',
  trim: '#c8c0a8',
  inner: '#d8d0bc',
  innerS: '#a89e88',
  sash: '#8a3a2a',
  sashS: '#5a2418',
  shortRobe: true,
  faceStyle: 'refSmirk',
};
/** The night watch: a dark tunic, a headband, a lantern and a clapper. */
LOOKS.watchman = {
  ...FOLK_BASE,
  hair: '#1a1410',
  hairH: '#3a2a20',
  hairStyle: 'topknot',
  headwear: 'band',
  accent: '#8a2a2a',
  outer: '#3a3a44',
  outerS: '#26262e',
  outerH: '#54545e',
  trim: '#8a2a2a',
  inner: '#6a6a74',
  innerS: '#4a4a52',
  sash: '#8a2a2a',
  sashS: '#5a1a1a',
  shortRobe: true,
  beard: 'stubble',
  faceStyle: 'refSerious',
};
/** ป้าเหมย: the tea seller, a green robe and a kind face. */
LOOKS.teaSeller = {
  ...FOLK_BASE,
  female: true,
  hair: '#3a2e2a',
  hairH: '#5a4a44',
  hairStyle: 'bun',
  headwear: 'pin',
  crown: '#5aa878',
  crownS: '#3a7a54',
  outer: '#4a7a5a',
  outerS: '#33573f',
  outerH: '#6a9a7a',
  trim: '#e8d8a8',
  inner: '#f0e8d0',
  innerS: '#c8bca0',
  sash: '#a87a3a',
  sashS: '#7a5424',
  faceStyle: 'refSmirk',
};
/** เสี่ยวเป่า: a town child with twin buns, always running somewhere. */
LOOKS.townKid = {
  ...FOLK_BASE,
  age: 'child',
  hair: '#1a1410',
  hairH: '#3a2a20',
  hairStyle: 'buns',
  headwear: 'none',
  outer: '#c8a83a',
  outerS: '#9a7e24',
  outerH: '#e8c85a',
  trim: '#5a8a3a',
  inner: '#f0e4c8',
  innerS: '#c8b89c',
  sash: '#5a8a3a',
  sashS: '#3a6a24',
  shortRobe: true,
  blush: '#f0a0a0',
  faceStyle: 'refSmirk',
};
/** A town girl (lane C0-NPC: the life children, shared data/npcLife.ts): twin buns with red ribbons, a pink jacket. */
LOOKS.townGirl = {
  ...FOLK_BASE,
  age: 'child',
  female: true,
  hair: '#1a1410',
  hairH: '#3a2a20',
  hairStyle: 'buns',
  headwear: 'none',
  outer: '#e07a8a',
  outerS: '#b0505e',
  outerH: '#f4a0ac',
  trim: '#c8343a',
  inner: '#f8ecd8',
  innerS: '#d0bea0',
  sash: '#c8343a',
  sashS: '#8a2026',
  shortRobe: true,
  blush: '#f4a0a8',
  faceStyle: 'refSoft',
};
/** นายท่ารถม้า (data/carriage.ts): a station master in a brown travelling coat, a straw hat, boots. */
LOOKS.stationMaster = {
  ...FOLK_BASE,
  hair: '#2a2018',
  hairH: '#4a3a2a',
  hairStyle: 'topknot',
  headwear: 'none',
  hat: { c: '#b69858', s: '#86703e' },
  outer: '#6a4a30',
  outerS: '#4a3220',
  outerH: '#8a6a48',
  trim: '#2e2018',
  inner: '#d8ccb0',
  innerS: '#a89c84',
  sash: '#3a4a5a',
  sashS: '#26323e',
  boots: '#2e2420',
  bootsS: '#1c1612',
  shortRobe: true,
  beard: 'stubble',
  faceStyle: 'refSerious',
};
